import { Configuration, CoreClient, CoreClientType } from '@givz/core-client';
import { useAuthContext } from '@hooks/auth';
import { useImpersonate } from '@hooks/impersonate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { version } from '../../../package.json';

export const useCoreClient = (props?: Configuration) => {
  const authContext = useAuthContext();
  const {
    accessToken: impersonateAccessToken,
    isLoggedIn: isImpersonateLoggedIn,
  } = useImpersonate();

  const config: Partial<Configuration> = useMemo(() => {
    let config: Partial<Configuration> = {
      accessToken: isImpersonateLoggedIn
        ? impersonateAccessToken
        : authContext?.accessToken,
    };
    if (props) {
      config = { ...config, ...props };
    }

    return config;
  }, [
    authContext?.accessToken,
    impersonateAccessToken,
    isImpersonateLoggedIn,
    props,
  ]);

  return useCallback(() => {
    return CoreClient(
      CORE_API_BASE_URL,
      `shopify-app@${version}; client`,
      config as Configuration,
    );
  }, [config]);
};

export type CoreClientFetch<T> = {
  coreClientFetch: (coreClient: CoreClientType) => any;
  dependencies?: (value?: T) => any[];
  initialValue?: T;
  onError?: (errors: any) => void;
  shouldFetch?: (value?: T) => boolean;
};

export const useCoreClientFetch = <T>({
  initialValue,
  onError = (errors) => console.error(errors),
  coreClientFetch,
  shouldFetch = () => true,
  dependencies = () => [],
}: CoreClientFetch<T>) => {
  const coreClient = useCoreClient();
  const [loading, setIsLoading] = useState(true);
  const [value, setValue] = useState<T | undefined>(initialValue);

  useEffect(() => {
    const fetchValues = async () => {
      setIsLoading(true);
      const { data, errors } = await coreClientFetch(coreClient());
      setIsLoading(false);
      if (errors) {
        onError(errors);
      } else {
        setValue(data);
      }
    };

    if (shouldFetch(value)) {
      fetchValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies(value));

  return {
    data: value,
    loading,
  };
};
